import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { ccoreApi } from "@/lib/ccore";
import { BrandLogo } from "@/components/common/BrandIdentity";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

export default function InviteAccept() {
  const { token } = useParams();
  const { auth, booting, ccore, reloadCcore, switchOrg } = useApp();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error | signin
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (booting) return;
    if (!auth) { setState("signin"); return; }
    if (!ccore?.ready) return;
    let alive = true;
    (async () => {
      try {
        const p = await ccoreApi.previewInvite(token);
        if (!alive) return;
        setPreview(p); setState("ready");
      } catch (err) {
        if (!alive) return;
        setMsg(err?.response?.data?.detail || "This invitation is invalid or has expired.");
        setState("error");
      }
    })();
    return () => { alive = false; };
  }, [auth, booting, ccore?.ready, token]);

  const accept = async () => {
    setBusy(true);
    try {
      const res = await ccoreApi.acceptInvite({ token });
      toast.success(`You joined ${preview?.orgName || "the organisation"}`);
      await reloadCcore();
      if (res?.membershipId) { try { await switchOrg(res.membershipId); } catch (e) { /* handled */ } }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept invitation");
      setBusy(false);
    }
  };
  const reject = async () => {
    setBusy(true);
    try { await ccoreApi.rejectInvite({ token }); toast.success("Invitation declined"); navigate("/dashboard"); }
    catch (err) { toast.error("Could not decline invitation"); setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6" data-testid="invite-accept-page">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3"><BrandLogo className="h-12 w-12" testId="invite-brand" /><div className="font-display text-2xl font-semibold text-ink">Organisation invitation</div></div>

        {state === "loading" && <p className="text-sm text-ink-2" data-testid="invite-loading">Loading invitation…</p>}

        {state === "signin" && (
          <div className="rounded-xl border border-line bg-card p-5" data-testid="invite-signin">
            <p className="text-sm text-ink-2">Please sign in with the account this invitation was sent to, then open the link again.</p>
            <Link to="/login"><Button className="mt-4 w-full" data-testid="invite-signin-btn">Sign in</Button></Link>
          </div>
        )}

        {state === "error" && <div className="rounded-xl border border-danger/30 bg-danger/5 p-5 text-sm text-danger" data-testid="invite-error">{msg}</div>}

        {state === "ready" && preview && (
          <div className="rounded-xl border border-line bg-card p-5" data-testid="invite-card">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-copper/10"><Building2 className="h-5 w-5 text-copper" /></div>
              <div>
                <div className="text-lg font-semibold text-ink" data-testid="invite-org">{preview.orgName}</div>
                <div className="text-[11px] uppercase tracking-wide text-ink-muted">Role: {preview.roleCode}</div>
              </div>
            </div>
            <p className="mt-4 text-sm text-ink-2">Invitation for <span className="font-medium text-ink">{preview.email}</span>.</p>
            {preview.canAccept ? (
              <div className="mt-5 flex gap-2">
                <Button variant="outline" className="flex-1" disabled={busy} onClick={reject} data-testid="invite-reject-btn">Decline</Button>
                <Button className="flex-1" disabled={busy} onClick={accept} data-testid="invite-accept-btn">Accept &amp; join</Button>
              </div>
            ) : (
              <div className="mt-5 rounded-lg border border-copper/30 bg-copper-light p-3 text-sm text-ink-2" data-testid="invite-cannot-accept">
                This invitation is addressed to a different account{preview.status !== "PENDING" ? ` or is ${preview.status.toLowerCase()}` : ""}. Sign in as <span className="font-medium">{preview.email}</span> to accept.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
